import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { AuthService } from './services/auth.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { HeaderComponent } from './components/header/header.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { BreadcrumbService } from './services/breadcrumb.service';
import { SettingsService } from './services/settings.service';
import {
  DisplayMode,
  getDisplayMode,
} from './components/item-detail/item-detail.types';
import packageJson from '../../package.json';

export interface BreadcrumbItem {
  label: string;
  url: string;
  icon?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    BreadcrumbModule,
    HeaderComponent,
    SidebarComponent,
  ],
})
export class AppComponent implements OnInit {
  @ViewChild('header') header!: HeaderComponent;

  menuItems: MenuItem[] = [];
  profileItems: MenuItem[] = [];
  staticMenuItems: MenuItem[] = [];
  isAuthenticated = false;
  currentYear = new Date().getFullYear();
  version = packageJson.version; // Default version from package.json
  breadcrumbItems: MenuItem[] = [];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  displayMode: DisplayMode = getDisplayMode();
  isDarkMode = localStorage.getItem('theme') === 'dark';
  currentTime = '';

  constructor(
    public authService: AuthService,
    public router: Router,
    private breadcrumbService: BreadcrumbService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private settingsService: SettingsService
  ) {
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
      this.updateMenuItems();
    });
    // Get version from global variable or fallback to package.json version
    this.version = (window as any).__APP_VERSION__ || packageJson.version;

    // Update resize listener
    window.addEventListener('resize', () => {
      this.displayMode = getDisplayMode();
    });

    // Apply theme on initialization
    //document.body.classList.toggle('dark', this.isDarkMode);
  }

  ngOnInit() {
    this.updateMenuItems();

    // Load auth setup to get naming settings
    // this.authService.loadAuthSetup().subscribe({
    //   next: () => {
    //     // Settings will be updated automatically via the auth service
    //     this.updateMenuItems();
    //   },
    //   error: (error) => {
    //     console.warn('Failed to load auth setup:', error);
    //     // Continue with default settings
    //     this.updateMenuItems();
    //   },
    // });

    // Subscribe to naming settings changes
    this.settingsService.namingSettings$.subscribe(() => {
      this.updateMenuItems();
    });

    // Subscribe to breadcrumbs
    this.breadcrumbService.breadcrumbsSubject
      .asObservable()
      .subscribe((items) => {
        this.breadcrumbItems = items;
      });

    this.updateCurrentTime();
    setInterval(() => {
      // Update current time every minute
      this.updateCurrentTime();
    }, 60000); // Update every minute
  }

  private updateCurrentTime() {
    this.currentTime =
      new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }) +
      ' ' +
      new Date().toLocaleDateString([], { month: '2-digit', day: '2-digit' });
  }

  private updateMenuItems() {
    if (this.isAuthenticated) {
      this.menuItems = [
        {
          label: 'Create a Post',
          icon: 'pi pi-pencil',
          routerLink: ['/post/new'],
        },
        {
          label: 'Discover',
          icon: 'pi pi-compass',
          routerLink: ['/feed'],
        },
        {
          label: 'Jobs',
          icon: 'pi pi-briefcase',
          routerLink: ['/jobs'],
        },
        {
          label: 'Chat',
          icon: 'pi pi-comments',
          routerLink: ['/chats'],
        },
        {
          label: 'Conference',
          icon: 'pi pi-calendar',
          routerLink: ['/conference'],
        },
      ];

      this.staticMenuItems = [];

      this.profileItems = [
        {
          label: 'Profile',
          icon: 'pi pi-user',
          routerLink: ['/profile'],
        },
        {
          separator: true,
        },
        {
          label: 'Sign Out',
          icon: 'pi pi-sign-out',
          command: () => {
            this.authService.logout();
            this.router.navigate(['/auth']);
          },
        },
      ];
    } else {
      this.menuItems = [];
      this.staticMenuItems = [];
      this.profileItems = [];
    }

    setTimeout(() => {
      // Use NgZone to properly schedule UI updates
      this.ngZone.run(() => {
        if (this.header) {
          this.header.menuItems = this.menuItems;
          this.header.staticMenuItems = this.staticMenuItems;
          this.header.profileItems = this.profileItems;
          this.header.isAuthenticated = this.isAuthenticated;
        }
      });
    }, 100);
  }

  isActiveRoute(route: string): boolean {
    const currentUrl = this.router.url || '';
    // Exact match
    if (currentUrl === route) {
      return true;
    }
    // Check if current URL starts with the route (for nested routes)
    // But avoid false positives (e.g., /profile should not match /profile-photo)
    if (currentUrl.startsWith(route + '/')) {
      return true;
    }
    // Special case for root feed route
    if (route === '/feed' && (currentUrl === '/' || currentUrl === '')) {
      return true;
    }
    return false;
  }
}
